import React, { useCallback, useEffect, useState } from "react";
import useLang from "Shared/hooks/useLanguage";
import { DeleteIcon } from "Shared/Icons";

type AddendumEntry = {
  id: string;
  date: string;
  text: string;
};

const formatDateForInput = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const createAddendum = (): AddendumEntry => ({
  id: `addendum-${Date.now()}`,
  date: formatDateForInput(new Date()),
  text: "",
});

type Props = {
  onHasAddendaChange?: (hasAddenda: boolean) => void;
};

const AddendaTab = ({ onHasAddendaChange }: Props) => {
  const { t } = useLang();
  const [addenda, setAddenda] = useState<AddendumEntry[]>([]);

  useEffect(() => {
    onHasAddendaChange?.(addenda.length > 0);
  }, [addenda.length, onHasAddendaChange]);

  const handleAdd = useCallback(() => {
    setAddenda((prev) => [...prev, createAddendum()]);
  }, []);

  const handleRemove = useCallback((id: string) => {
    setAddenda((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const handleUpdate = useCallback(
    (id: string, patch: Partial<Pick<AddendumEntry, "date" | "text">>) => {
      setAddenda((prev) =>
        prev.map((a) => (a.id === id ? { ...a, ...patch } : a))
      );
    },
    []
  );

  return (
    <>
      <style>{`
        .pathology-addendum-card {
          border: 1px solid #e4e6ef;
          border-radius: 10px;
          overflow: hidden;
          background: #fff;
          margin-bottom: 1rem;
        }
        .pathology-addendum-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background-color: #f3f6f9;
          border-bottom: 1px solid #e4e6ef;
          padding: 10px 16px;
        }
        .pathology-addendum-card-title {
          font-weight: 600;
          color: #3f4254;
          font-size: 0.95rem;
        }
        .pathology-addendum-delete {
          border: none;
          background: transparent;
          padding: 4px 8px;
          cursor: pointer;
          line-height: 1;
        }
        .pathology-addendum-delete:hover {
          opacity: 0.85;
        }
        .pathology-addendum-card-body {
          padding: 16px;
        }
        .pathology-addendum-date.form-control {
          max-width: 220px;
        }
        textarea.pathology-addendum-text.form-control {
          height: auto !important;
          min-height: 120px;
          margin-top: 12px;
        }
        .pathology-addenda-empty {
          border: 2px dashed #d1d5db;
          background-color: #fafafa;
          border-radius: 10px;
        }
      `}</style>

      <div>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h5 className="fw-bold text-gray-800 mb-0">
            {t("Addenda & amendments")}
          </h5>
          <button
            type="button"
            className="btn btn-sm btn-primary fw-semibold px-4"
            onClick={handleAdd}
          >
            {t("+ Add addendum")}
          </button>
        </div>

        {addenda.length === 0 ? (
          <div className="pathology-addenda-empty d-flex flex-column align-items-center justify-content-center py-10 px-4">
            <i
              className="bi bi-file-earmark-text text-gray-400"
              style={{ fontSize: "2.5rem" }}
            />
            <div className="fw-semibold text-gray-700 mt-3">
              {t("No addenda yet")}
            </div>
            <div
              className="text-gray-500 fs-7 mt-1 text-center"
              style={{ maxWidth: 420 }}
            >
              {t(
                "Addenda appear in the final report with date stamps, exactly like the current PDF format"
              )}
            </div>
          </div>
        ) : (
          addenda.map((entry, index) => (
            <div key={entry.id} className="pathology-addendum-card">
              <div className="pathology-addendum-card-header">
                <span className="pathology-addendum-card-title">
                  {t("Addendum")} {index + 1}
                </span>
                <button
                  type="button"
                  className="pathology-addendum-delete"
                  aria-label={t("Delete addendum")}
                  onClick={() => handleRemove(entry.id)}
                >
                  {DeleteIcon("bi bi-trash text-danger fs-5")}
                </button>
              </div>
              <div className="pathology-addendum-card-body">
                <input
                  type="date"
                  className="form-control rounded-2 pathology-addendum-date"
                  value={entry.date}
                  onChange={(e) =>
                    handleUpdate(entry.id, { date: e.target.value })
                  }
                />
                <textarea
                  className="form-control rounded-2 pathology-addendum-text"
                  placeholder={t("Addendum text....")}
                  value={entry.text}
                  onChange={(e) =>
                    handleUpdate(entry.id, { text: e.target.value })
                  }
                />
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default AddendaTab;
